import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { RelayEnvironmentProvider } from "react-relay";
import relayEnvironment from "./relayEnvironment";
import "./index.css";
import Quote from "./pages/quote";
import { AuthenticationProvider } from "./components/admin/authentication/AuthenticationProvider";
import { AdminHomeRedirect } from "./components/admin/authentication/AdminHomeRedirect";
import { BusinessLocationProvider } from "./components/admin/business_location/BusinessLocationProvider";
import { GAProvider } from "./components/providers/GAProvider";
import { AppConfigProvider } from "./components/providers/AppConfigProvider";
import { frontEnv } from "./env";
import { FeatureFlagsProvider } from "./components/providers/FeatureFlagsProvider";
import { ABTestProvider } from "./components/providers/ABTestProvider";
import { ABTestResolutionProvider } from "./components/providers/ABTestResolutionProvider";
import { WorkspaceProvider } from "./components/admin/workspace/WorkspaceProvider";
import { WellnessCustomizationProvider } from "./components/admin/workspace/WellnessCustomizationProvider";
import { lazy, Suspense } from "react";
import { Spin } from "antd";
// Route-level error boundary for the livestream admin routes (errorElement).
// EAGER, not lazy(): a boundary whose own chunk has to be fetched cannot render
// when a failed fetch is what threw — it would suspend and hand the page back
// to React Router's stock error screen, i.e. exactly the thing it exists to
// replace. A handful of lines against antd, which the admin chunk already
// carries, so this costs the funnel bundle nothing.
import AdminRouteError from "./components/admin/AdminRouteError";
const BookingSuccessPage = lazy(() => import("./pages/BookingSuccessPage"));
const BookingCancelledPage = lazy(() => import("./pages/BookingCancelledPage"));
const AcceptEstimatePage = lazy(() => import("./pages/AcceptEstimatePage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookDirectPage = lazy(() => import("./pages/BookDirectPage"));
const PayInvoiceLinkPage = lazy(() => import("./pages/PayInvoiceLinkPage"));
// Campaign action links — one-tap photo upload + financing trust warm-up.
// Each wraps its own heavy content in an internal Suspense (mirrors
// sign_envelope.tsx) — see the CampaignPhotoLoadingShell /
// CampaignFinancingLoadingShell imports + Content lazy() calls below.
const CampaignPhotoPage = lazy(() => import("./pages/CampaignPhotoPage"));
const CampaignFinancingPage = lazy(
  () => import("./pages/CampaignFinancingPage"),
);
// Booking Calendar — patient-facing Tier Pricing entry at /booking-calendar. Mock
// scaffold (see client/src/components/booking-calendar/ + docs/features/
// tier-pricing-booking/06_FRONTEND_SCAFFOLD.md). Follows the
// PATIENT_FACING_EMAIL_LINKS.md checklist: explicit route declared above
// /* below, defensive intercept in PublicCatchAllDispatcher, and the
// labelled loading shell is exported from the page module.
const BookingCalendarPage = lazy(() => import("./pages/BookingCalendarPage"));
// Public livestream viewer — patient-facing marketing URL at /live (NOT
// under /action/, same precedent as Booking Calendar above: it's a
// marketing entry point, not a token-gated email link). Follows the
// PATIENT_FACING_EMAIL_LINKS.md checklist: explicit route declared below
// /*, defensive intercept in PublicCatchAllDispatcher, and the labelled
// LiveStreamLoadingShell is exported from the page module (statically
// imported here so index.tsx can render it without dragging in the heavy
// LiveStreamApp/amazon-ivs-player chunk). See
// docs/livestreaming/plan.md § "2026-07-21 — Web-first V1 pivot".
const LiveStreamPage = lazy(() => import("./pages/LiveStreamPage"));
// Public livestream REPLAY viewer at /live/replay/:eventId, the recording of
// an ended broadcast, and the URL we text a viewer who asked for the link
// (Group R · ES2). Same checklist as /live directly above: explicit route
// declared below, defensive intercept in PublicCatchAllDispatcher, and a
// labelled LiveReplayLoadingShell statically imported from the page module.
const LiveReplayPage = lazy(() => import("./pages/LiveReplayPage"));
// OBS Browser Source overlay at /live/overlay: the on-air question card
// composited over the presenter's camera. Same lazy pattern, but with its own
// SILENT shell and boundary (they render nothing, never a label or an error
// screen) because no viewer watches this route, only a broadcast. See
// pages/LiveOverlayPage.tsx and docs/superpowers/specs/
// 2026-10-16-livestream-onair-overlay-design.md.
const LiveOverlayPage = lazy(() => import("./pages/LiveOverlayPage"));
// Password-reset page — patient-facing email-link surface (P0-2).
// Follows PATIENT_FACING_EMAIL_LINKS.md: explicit route, catch-all intercept,
// and exported ResetPasswordLoadingShell (defined in the page module).
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
// Expired-quote-link "Request a new link" page. Patient-facing email-link route
// (PATIENT_FACING_EMAIL_LINKS.md): explicit route below /* + catch-all intercept
// + QuoteExpiredLoadingShell + /action/ curtain auto-exclusion.
const QuoteExpiredPage = lazy(() => import("./pages/QuoteExpiredPage"));
// WellnessCami intake — patient-facing token-gated form (P5).
// PATIENT_FACING_EMAIL_LINKS.md: explicit route below /* + catch-all intercept
// + WellnessIntakeLoadingShell + /action/ curtain auto-exclusion.
const WellnessIntakePage = lazy(() => import("./pages/wellness_intake"));
import "./i18n";
import { persistTrackingFromUrl } from "./utils/tracking";
import {
  installGlobalErrorHandlers,
  emitBootstrapPing,
  trackEvent,
} from "./utils/clientEvents";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { patientPortalRoutes } from "./routes/PatientPortalRoutes";
import { dynamicFormRoutes } from "./components/admin/routes/dynamic_form/DynamicFormRoutes";
import {
  todoRoutes,
  TodoLoadingShell,
  MedicalHistoryLoadingShell,
  TodoSignRedirect,
} from "./routes/TodoRoutes";
import { SignerLoadingShell } from "./pages/patient-portal/sign_envelope";
import { CampaignPhotoLoadingShell } from "./pages/CampaignPhotoPage";
import { CampaignFinancingLoadingShell } from "./pages/CampaignFinancingPage";
import { LiveStreamLoadingShell } from "./pages/LiveStreamPage";
import { LiveReplayLoadingShell } from "./pages/LiveReplayPage";
// Eager for the same reason: used as a Suspense fallback and the overlay
// route's errorElement, so it must render without the lazy chunk.
import {
  OverlayLoadingShell,
  OverlaySilentBoundary,
} from "./pages/LiveOverlayPage";
// Eager like the loading shell above, and for the same reason: an errorElement
// cannot be lazy — the chunk that failed to load may be why it is rendering.
// The module is inline-styled and imports no CSS, so it costs the funnel
// nothing beyond its own few hundred bytes.
import LiveRouteError, {
  LiveRouteErrorBoundary,
} from "./components/livestream/LiveRouteError";
// SignerApp (react-pdf/pdfjs) and PublicFillFormPage (admin FormFieldRenderer)
// are lazy() so anonymous quote-funnel visitors don't download them. Each is
// rendered only inside a <Suspense> in PublicCatchAllDispatcher, and the eager
// LoadingShell imports above are kept free of static heavy imports (see
// TodoRoutes.tsx + sign_envelope.tsx). docs/infrastructure/CLIENT_BUNDLE_ARCHITECTURE.md
const PublicFillFormPage = lazy(
  () => import("./components/todo/PublicFillFormPage"),
);
const PublicMedicalHistoryPage = lazy(
  () => import("./components/todo/PublicMedicalHistoryPage"),
);
const SignerApp = lazy(
  () => import("./components/patient-portal/contracts/SignerApp"),
);
// Campaign action links' heavy content — lazy-loaded separately from the
// CampaignPhotoPage/CampaignFinancingPage wrappers so PublicCatchAllDispatcher
// can render them directly with tokenOverride (mirrors SignerApp above).
const CampaignPhotoContent = lazy(() => import("./pages/CampaignPhotoContent"));
const CampaignFinancingContent = lazy(
  () => import("./pages/CampaignFinancingContent"),
);

// Admin-only pages — lazy-loaded so anonymous quote-funnel visitors don't
// pay the download/parse cost. The root <Suspense fallback={null}> below
// catches the chunk load; no per-route Suspense needed.
const ProceduresDashboard = lazy(() => import("./pages/procedures_dashboard"));
const SurgeonDashboard = lazy(() => import("./pages/surgeon_dashboard"));
const ProcedureTypesDashboard = lazy(
  () => import("./pages/procedure_types_dashboard"),
);
const BusinessLocationsDashboard = lazy(
  () => import("./pages/business_locations_dashboard"),
);
const UserDashboard = lazy(() => import("pages/admin/users_dashboard"));
// The OBS Browser Source page loads once and never navigates, so reading the
// path at boot is enough.
const IS_OBS_OVERLAY = /\/live\/overlay(?:\/|$)/.test(window.location.pathname);
const ABTestDebugWindow = lazy(
  () => import("./components/admin/ABTestDebugWindow"),
);
const ABTestResultsDashboard = lazy(
  () => import("./pages/ab_test_results_dashboard"),
);
const PromoCodesDashboard = lazy(() => import("./pages/promo_codes_dashboard"));
const PatientsActivityPage = lazy(() => import("./pages/patients_activity"));
const RoadmapDashboard = lazy(() => import("./pages/admin/roadmap_dashboard"));
const WellnessHomePage = lazy(() => import("./pages/wellness_home"));
const WellnessCustomizationPage = lazy(
  () => import("./pages/wellness_customization"),
);
// WellnessCami portal sections (wellness workspace). Rows mirror the
// canonical build plan's final admin nav (docs/wellness-portal/00_INDEX.md §5);
// planned-but-unbuilt sections render a WellnessComingSoon placeholder.
const WellnessOverviewPage = lazy(() => import("./pages/wellness/overview"));
const WellnessRecipesPage = lazy(() => import("./pages/wellness/recipes"));
const WellnessClientsPage = lazy(() => import("./pages/wellness/clients"));
const WellnessPatientCalendarPage = lazy(
  () => import("./pages/wellness/patient_calendar"),
);
const WellnessIntakeAdminPage = lazy(() => import("./pages/wellness/intake"));
const WellnessMessagingPage = lazy(() => import("./pages/wellness/messaging"));

// Full-viewport labelled loaders for the public token-gated pages
// (booking-fee + estimate-acceptance). Mirrors TodoLoadingShell /
// SignerLoadingShell: gray 100vh background so the funnel chrome never
// bleeds through, plus a single line of plain-language label (a silent
// spinner reads as broken). Defined HERE rather than exported from the
// page modules so those modules stay lazy() — funnel visitors (the 99%
// case) never download booking/estimate code. Used as the Suspense
// fallback in BOTH the explicit route and the /* catch-all branch so the
// two code paths show an identical loader.
// See docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
const PublicLinkLoadingShell = ({ label }: { label: string }) => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 16,
    }}
  >
    <Spin size="large" />
    <div style={{ color: "#666", fontSize: 14 }}>{label}</div>
  </div>
);
const BookingLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading your booking…" />
);
const BookDirectLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading your booking…" />
);
const AcceptEstimateLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading your estimate…" />
);
const PayInvoiceLinkLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading your invoice…" />
);
const BookingCalendarLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading the booking calendar…" />
);
const ResetPasswordLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading password reset…" />
);
const QuoteExpiredLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading…" />
);
const WellnessIntakeLoadingShell = () => (
  <PublicLinkLoadingShell label="Loading your wellness intake form…" />
);
// Admin (internal) full-viewport loader for the lazy SalesViewV2Page routes.
// These three route elements are otherwise the only lazy admin pages with NO
// per-route <Suspense>, so a chunk-load suspend falls through to the app-root
// <Suspense fallback={null}> and blanks the whole page. Reuses the same
// Spin+label shell as the public routes with neutral, non-patient-facing copy.
const SalesViewLoadingShell = () => <PublicLinkLoadingShell label="Loading…" />;

// Defensive dispatcher for the root /* catch-all. React Router 6 ranks
// routes by specificity in theory, but explicit patient-facing routes
// (/action/book/:token, /action/accept-estimate/:token,
// /action/todo/form/:linkToken, /portal/sign/:envelopeId) were empirically
// losing to /* on staging — patients ended up briefly on the funnel
// landing before realizing they were on the wrong page. This wrapper
// intercepts those URLs at the catch-all level so the right component
// renders even if React Router's matcher picks the splat. The optional
// (?:action/)? in each pattern also catches any pre-/action/ link.
//
// Add every new patient-facing email-link route here too — see
// docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
//
// We check BOTH the React-Router-stripped pathname AND the raw
// window.location.pathname — defensive against any case where the
// basename strip doesn't behave as expected.

// /book → /booking-calendar redirect, preserving the query string so a
// stale link like /book?tier=2 keeps its (future) calendar filter preset.
const BookRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/booking-calendar", search }} replace />;
};

// Two Lanes (2026-08-12): /admin/preop/documents (the retired global documents
// queue) → the filing lane, preserving `search` so any query the old page took
// survives the hop. Same shape as BookRedirect above.
const PreOpDocumentsRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/admin/preop/inbox", search }} replace />;
};

// Owner decision 2026-08-28: the standalone Clear Labs lane
// (/admin/preop/clearances) is retired — clearing a whole queue of patients'
// labs from one cross-patient screen is "too confusing and prone to errors
// without the context of the patients". This extends #1649 (2026-08-14), which
// removed the lane's UI entry points but left both lane routes fully live and
// URL-reachable; for clearing specifically the route itself is now a redirect.
// The case workspace's Labs Review tab (the embedded CaseClearances mount) is the
// canonical clearing surface. `search` is preserved for symmetry with the
// redirects above — a stale `?case=` no longer scopes anything, but dropping
// the query silently is worse than carrying it.
const PreOpClearancesRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/admin/preop", search }} replace />;
};

const PublicCatchAllDispatcher = () => {
  const { pathname } = useLocation();
  const rawPath = typeof window !== "undefined" ? window.location.pathname : "";
  // Combine both so the regex catches the patient-facing URLs whether
  // or not the basename has been stripped — defensive against React
  // Router edge cases where useLocation().pathname includes the
  // basename prefix.
  const candidate = `${pathname} ${rawPath}`;
  // Patient action pages now live under /action/ (e.g. /action/book/:token).
  // The optional (?:action/)? keeps any pre-/action/ link still resolving to
  // the right component instead of falling through to <Quote />.
  const fillFormMatch = candidate.match(
    /\/(?:action\/)?todo\/form\/([^/?#\s]+)/,
  );
  if (fillFormMatch) {
    return (
      <Suspense fallback={<TodoLoadingShell />}>
        <PublicFillFormPage linkTokenOverride={fillFormMatch[1]} />
      </Suspense>
    );
  }
  // PreOp medical-history questionnaire (Phase 2). Mirrors the todo/form/
  // branch above. See docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
  const medHxMatch = candidate.match(
    /\/(?:action\/)?todo\/medical-history\/([^/?#\s]+)/,
  );
  if (medHxMatch) {
    return (
      <Suspense fallback={<MedicalHistoryLoadingShell />}>
        <PublicMedicalHistoryPage linkTokenOverride={medHxMatch[1]} />
      </Suspense>
    );
  }
  // SIGN_CONTRACT to-do links copied from the admin before 2026-10-23 carry
  // /action/todo/sign/<token>, a shape no page ever served; bounce them to the
  // server bridge (routes/todo-sign-redirect.ts). See TodoRoutes.tsx.
  const signTodoMatch = candidate.match(
    /\/(?:action\/)?todo\/sign\/([^/?#\s]+)/,
  );
  if (signTodoMatch) {
    return <TodoSignRedirect linkTokenOverride={signTodoMatch[1]} />;
  }
  const signEnvelopeMatch = candidate.match(/\/portal\/sign\/([^/?#\s]+)/);
  if (signEnvelopeMatch) {
    return (
      <Suspense fallback={<SignerLoadingShell />}>
        <SignerApp envelopeIdOverride={signEnvelopeMatch[1]} />
      </Suspense>
    );
  }
  const acceptEstimateMatch = candidate.match(
    /\/(?:action\/)?accept-estimate\/([^/?#\s]+)/,
  );
  if (acceptEstimateMatch) {
    return (
      <Suspense fallback={<AcceptEstimateLoadingShell />}>
        <AcceptEstimatePage tokenOverride={acceptEstimateMatch[1]} />
      </Suspense>
    );
  }
  // Campaign one-click booking. Checked BEFORE bookingLinkMatch — its token
  // segment is "book-direct/<token>", distinct from "book/<token>", so the two
  // regexes don't overlap, but keep this first for clarity. See
  // server/routes/book-direct.ts and PATIENT_FACING_EMAIL_LINKS.md.
  const bookDirectMatch = candidate.match(
    /\/(?:action\/)?book-direct\/([^/?#\s]+)/,
  );
  if (bookDirectMatch) {
    return (
      <Suspense fallback={<BookDirectLoadingShell />}>
        <BookDirectPage tokenOverride={bookDirectMatch[1]} />
      </Suspense>
    );
  }
  const bookingLinkMatch = candidate.match(/\/(?:action\/)?book\/([^/?#\s]+)/);
  if (bookingLinkMatch) {
    return (
      <Suspense fallback={<BookingLoadingShell />}>
        <BookingPage tokenOverride={bookingLinkMatch[1]} />
      </Suspense>
    );
  }
  // /booking-calendar(/step)? → patient-facing Booking Calendar (Tier
  // Pricing entry; step segments are parsed inside the app). The /admin/
  // guard keeps the admin inventory page (/admin/booking-calendar) out of
  // this public branch. Per PATIENT_FACING_EMAIL_LINKS.md, this defensive
  // branch mirrors the explicit route declared below; it's the safety net
  // for cases where React Router 6's specificity ranking picks /* over
  // the explicit route.
  if (
    /\/booking-calendar(?:\/|\s|$)/.test(candidate) &&
    !/\/admin\//.test(candidate)
  ) {
    return (
      <Suspense fallback={<BookingCalendarLoadingShell />}>
        <BookingCalendarPage />
      </Suspense>
    );
  }
  // Former Booking Calendar base URL. Only the BARE /book lands here —
  // legacy /book/:token payment links matched the token branch above.
  if (/\/book\/?(?:[?#].*)?$/.test(candidate)) {
    return <BookRedirect />;
  }
  // Public livestream REPLAY, ABOVE the /live branch below, whose regex would
  // otherwise swallow this URL and mount the live viewer on it. The id is read
  // out of the path here rather than from route params: this branch renders
  // outside the explicit route, so there are none.
  const liveReplayMatch = candidate.match(/\/live\/replay\/([0-9a-f]{24})/i);
  if (liveReplayMatch && !/\/admin\//.test(candidate)) {
    return (
      // The `/*` route has no errorElement, so this copy of the page carries
      // its own boundary, same one the explicit route wraps below.
      <LiveRouteErrorBoundary>
        <Suspense fallback={<LiveReplayLoadingShell />}>
          <LiveReplayPage eventIdOverride={liveReplayMatch[1]} />
        </Suspense>
      </LiveRouteErrorBoundary>
    );
  }
  // OBS overlay, ABOVE the /live branch below for the same reason as the replay
  // branch: that regex also matches /live/overlay. index.html's curtain already
  // skips /live/*, and OverlayPage calls hideInitialLoading() on mount too.
  if (
    /\/live\/overlay(?:\/|\?|\s|$)/.test(candidate) &&
    !/\/admin\//.test(candidate)
  ) {
    return (
      <OverlaySilentBoundary>
        <Suspense fallback={<OverlayLoadingShell />}>
          <LiveOverlayPage />
        </Suspense>
      </OverlaySilentBoundary>
    );
  }
  // Public livestream viewer — patient-facing marketing URL (topaesthetics.com/live
  // redirects into the SPA here; see sites/topaesthetics.com/.htaccess and
  // docs/livestreaming/plan.md § "2026-07-21 — Web-first V1 pivot"). Mirrors
  // the /booking-calendar branch above: explicit route + this defensive
  // catch-all intercept + labelled loading shell + index.html curtain
  // exclusion. The /admin/ guard keeps any future admin livestream-management
  // surface out of this public branch.
  if (/\/live(?:\/|\s|$)/.test(candidate) && !/\/admin\//.test(candidate)) {
    return (
      // The `/*` route has no errorElement, so this copy of the viewer carries
      // its own boundary, same one the explicit route wraps below.
      <LiveRouteErrorBoundary>
        <Suspense fallback={<LiveStreamLoadingShell />}>
          <LiveStreamPage />
        </Suspense>
      </LiveRouteErrorBoundary>
    );
  }
  const payInvoiceMatch = candidate.match(
    /\/(?:action\/)?pay-invoice\/([^/?#\s]+)/,
  );
  if (payInvoiceMatch) {
    return (
      <Suspense fallback={<PayInvoiceLinkLoadingShell />}>
        <PayInvoiceLinkPage tokenOverride={payInvoiceMatch[1]} />
      </Suspense>
    );
  }
  // Password reset page — single-use 30-min token from the emailed link (P0-2).
  const resetPasswordMatch = candidate.match(
    /\/action\/reset-password\/([^/?#\s]+)/,
  );
  if (resetPasswordMatch) {
    return (
      <Suspense fallback={<ResetPasswordLoadingShell />}>
        <ResetPasswordPage tokenOverride={resetPasswordMatch[1]} />
      </Suspense>
    );
  }
  // Expired quote resume link → "Request a new link". The funnel redirects here
  // when the server reports an authentic-but-lapsed resume token.
  const quoteExpiredMatch = candidate.match(
    /\/action\/quote-expired\/([^/?#\s]+)/,
  );
  if (quoteExpiredMatch) {
    return (
      <Suspense fallback={<QuoteExpiredLoadingShell />}>
        <QuoteExpiredPage tokenOverride={quoteExpiredMatch[1]} />
      </Suspense>
    );
  }
  const wellnessIntakeMatch = candidate.match(
    /\/(?:action\/)?wellness\/intake\/([^/?#\s]+)/,
  );
  if (wellnessIntakeMatch) {
    return (
      <Suspense fallback={<WellnessIntakeLoadingShell />}>
        <WellnessIntakePage tokenOverride={wellnessIntakeMatch[1]} />
      </Suspense>
    );
  }
  // Campaign action links — one-tap photo upload + financing trust warm-up.
  // See server/routes/campaign-action-routes.ts and
  // docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
  const campaignPhotoMatch = candidate.match(
    /\/(?:action\/)?upload-photos\/([^/?#\s]+)/,
  );
  if (campaignPhotoMatch) {
    return (
      <Suspense fallback={<CampaignPhotoLoadingShell />}>
        <CampaignPhotoContent tokenOverride={campaignPhotoMatch[1]} />
      </Suspense>
    );
  }
  const campaignFinancingMatch = candidate.match(
    /\/(?:action\/)?apply-financing\/([^/?#\s]+)/,
  );
  if (campaignFinancingMatch) {
    return (
      <Suspense fallback={<CampaignFinancingLoadingShell />}>
        <CampaignFinancingContent tokenOverride={campaignFinancingMatch[1]} />
      </Suspense>
    );
  }
  return <Quote />;
};
const ContractTemplateEditorDashboard = lazy(
  () => import("./pages/admin/contract_editor_dashboard"),
);
const ContractTemplatesDashboard = lazy(
  () => import("./pages/admin/contract_templates_dashboard"),
);
const SmsTemplatesDashboard = lazy(
  () => import("./pages/admin/sms_templates_dashboard"),
);
const LabsRequestTextTemplatesDashboard = lazy(
  () => import("./pages/admin/labs_request_text_templates_dashboard"),
);
const StatusesDashboard = lazy(
  () => import("./pages/admin/statuses_dashboard"),
);
const RingCentralAuthPage = lazy(
  () => import("./pages/admin/ringcentral_auth"),
);
const QuickBooksSettingsPage = lazy(
  () => import("./pages/admin/quickbooks_settings"),
);
const QBOSyncStatusPage = lazy(
  () => import("./pages/admin/quickbooks_sync_status"),
);
const FeatureFlagsAdminPage = lazy(
  () => import("./pages/admin/feature_flags_page"),
);
const MedicalStaffAdminPage = lazy(
  () => import("./pages/admin/medical_staff_page"),
);
// Meta Campaign Manager — ADMIN-only settings page (fences, approval mode,
// allowed actions). Dark behind CAMPAIGN_MANAGER_ENABLED (MainMenu.tsx +
// campaignManagerResolver.ts). See also CampaignManagerDashboardPage below.
const CampaignManagerSettingsAdminPage = lazy(
  () => import("./pages/admin/campaign_manager_settings"),
);
const VoiceAgentPage = lazy(() => import("./pages/admin/voice_agent_page"));
const ReconciliationDashboardPage = lazy(
  () => import("./pages/admin/reconciliation_dashboard"),
);
const AddonsDashboard = lazy(() => import("pages/admin/addons_dashboard"));
const ProductsDashboard = lazy(() => import("pages/admin/products_dashboard"));
const LabsDashboard = lazy(() => import("pages/admin/labs_dashboard"));
const PostOpLocationsDashboard = lazy(
  () => import("pages/admin/postop_locations"),
);
const PostOpVisitsDashboard = lazy(() => import("pages/admin/postop_visits"));
const CalendarDashboard = lazy(() => import("pages/admin/calendar_dashboard"));
const CoordinatorViewDashboard = lazy(
  () => import("pages/admin/coordinator_view_dashboard"),
);
const PhotoLibraryPage = lazy(() => import("pages/admin/photo_library"));
const ConfigsDashboard = lazy(() => import("pages/admin/configs_dashboard"));
const MarketingDashboardV2Page = lazy(
  () => import("./pages/marketing_dashboard_v2"),
);
// Active Campaigns — read-only snapshot of what's currently live across
// Google Ads, Meta Ads, and SMS/Email (server/schema/_activeCampaigns.graphql).
// No spend/CPL metrics — that's Marketing (v2) above.
const ActiveCampaignsDashboardPage = lazy(
  () => import("./pages/admin/active_campaigns_dashboard"),
);
// Content Engagement — how organic Instagram / Facebook content performs over
// time (server/schema/_contentEngagement.graphql). Mantine page; data syncs
// once a day, so no polling or realtime wiring.
const ContentEngagementDashboardPage = lazy(
  () => import("./pages/admin/content_engagement_dashboard"),
);
// Team Engagement — our employees' engagement with our Instagram posts, a
// month-to-date leaderboard with a monthly winner
// (server/schema/_employeeEngagement.graphql). Mantine page; data syncs once
// a day, so no polling or realtime wiring.
const EmployeeEngagementDashboardPage = lazy(
  () => import("./pages/admin/employee_engagement_dashboard"),
);
// Meta Campaign Manager — the operator chat page (Claude on Bedrock reads
// and changes Meta campaigns inside admin-set fences). Dark behind
// CAMPAIGN_MANAGER_ENABLED (MainMenu.tsx + campaignManagerResolver.ts). See
// also CampaignManagerSettingsAdminPage above.
const CampaignManagerDashboardPage = lazy(
  () => import("./pages/admin/campaign_manager"),
);
const ThirdPartyUsageDashboardPage = lazy(
  () => import("./pages/third_party_usage_dashboard"),
);
const GalleryPhotosDashboardPage = lazy(
  () => import("pages/admin/gallery_photos_dashboard"),
);
const AcknowledgementsDashboardPage = lazy(
  () => import("pages/admin/acknowledgements_dashboard"),
);
// Guides (Content Library behind the REVIEW_GUIDE to-do) — the library list and
// the full-page editor. Both page components own their own DashboardContainer +
// local AdminMantineProvider, so the pages/ entries are pass-throughs.
const GuidesPage = lazy(() => import("pages/admin/guides"));
const GuideEditorPage = lazy(() => import("pages/admin/guide_editor"));
const TestimonialsDashboardPage = lazy(
  () => import("pages/admin/testimonials_dashboard"),
);
const ResourcesDashboardPage = lazy(
  () => import("pages/admin/resources_dashboard"),
);
const UsabilityDashboardPage = lazy(
  () => import("./pages/admin/usability_dashboard"),
);
const IosAnalyticsPage = lazy(() => import("./pages/admin/ios_analytics"));
const TrafficDashboardPage = lazy(
  () => import("./pages/admin/traffic_dashboard"),
);
const UsabilityQuoteDrilldownPage = lazy(
  () => import("./pages/admin/usability_quote_drilldown"),
);
// Funnel Flow Sankey (admin). Lazy like every other dashboard — it pulls
// recharts, which must never reach the public funnel's eager entry chunk
// (client/scripts/check-funnel-bundle.js fails the build if it does).
const FunnelFlowDashboardPage = lazy(
  () => import("./pages/admin/funnel_flow_dashboard"),
);
const LiveDashboardPage = lazy(() => import("./pages/admin/live_dashboard"));
// Value-based sibling (2026-08-28) — same wallboard, booked dollar value as
// the headline numbers instead of booking counts. See LiveWallboard.tsx.
const LiveValueDashboardPage = lazy(
  () => import("./pages/admin/live_value_dashboard"),
);
// Livestream admin surface (docs/livestreaming/plan.md). Admin-session-gated
// server-side (requireAdminSafe in liveEventResolver.ts — any admin-side staff
// role incl. MARKETING, not the ADMIN role alone); no client-side role
// check here, same convention as Sales Monitoring / Financing Review. The
// routes live under /admin/livestream/* — deliberately distinct from the
// existing /admin/live (LiveDashboardPage, the sales wallboard) directly above
// so there's no route-path ambiguity between the two.
//
// The panel split (PR 3) replaced the single morphing console with Overview
// (scheduling/planning) + Dashboard (broadcasting), and PR 5 added Analytics
// (post-hoc, what happened on a stream that already ran). Oct 17 2026 (spec
// 2026-10-17-livestream-stream-dashboard-design.md, R2): the new all-in-one
// Stream dashboard takes /admin/livestream/dashboard, the old Dashboard is
// Stream overview at /admin/livestream/overview and the old Overview is Stream
// planning at /admin/livestream/planning. Component files keep their names.
// Bare /admin/livestream redirects to the Stream dashboard.
const LivestreamOverviewPage = lazy(
  () => import("./pages/admin/LivestreamOverviewPage"),
);
const LivestreamDashboardPage = lazy(
  () => import("./pages/admin/LivestreamDashboardPage"),
);
const LivestreamStreamDashboardPage = lazy(
  () => import("./pages/admin/LivestreamStreamDashboardPage"),
);
// Post-hoc analytics (PR 5). Lazy like every other charting dashboard — it
// pulls @mantine/charts/recharts, which must never reach the public funnel's
// eager entry chunk (client/scripts/check-funnel-bundle.js fails the build if
// it does).
const LivestreamAnalyticsPage = lazy(
  () => import("./pages/admin/LivestreamAnalyticsPage"),
);
// Subscribers view (PR-B, reminder-v2 plan) — the cross-lane roster that
// replaced the Signups section on Stream planning.
const LivestreamSubscribersPage = lazy(
  () => import("./pages/admin/LivestreamSubscribersPage"),
);
// Promotion (2026-10-17 spec): link results now, the texts' and emails'
// wording from PR C.
const LivestreamPromotionPage = lazy(
  () => import("./pages/admin/LivestreamPromotionPage"),
);
// Minimal-chrome chat window opened by the chat rail's "↗ Pop out" button —
// no sidebar/header (see the page's own comment).
const LivestreamChatPopoutPage = lazy(
  () => import("./pages/admin/LivestreamChatPopoutPage"),
);
const ActionQueuesPage = lazy(
  () => import("./components/admin/action_queues/ActionQueuesPage"),
);
const PreOpDashboardPage = lazy(
  () => import("./components/admin/preop_dashboard/PreOpDashboardPage"),
);
// Mantine case workspace (2026-07-30 labs-review redesign PR 2/3) — the
// dashboard's row click navigates here. Three patient-scoped tabs (Details,
// Documents, Labs): it is both the chart you consult AND where a single
// patient's filing and clearing happen, over the same machinery the two
// global lanes below render for the whole queue. It consumes `?doc=` itself
// (Labs Intake tab, that document pre-selected).
const PreOpCaseWorkspacePage = lazy(
  () => import("./components/admin/preop_workspace/PreOpCaseWorkspacePage"),
);
// Two Lanes (2026-08-12): filing and clearing are never on one screen. The
// global documents queue these replace (PreOpDocumentsQueuePage) is deleted;
// /admin/preop/documents redirects to the inbox. Filing is the only one of the
// two that still has a standalone page — the clearing lane's shell was deleted
// on 2026-08-28 and /admin/preop/clearances redirects (PreOpClearancesRedirect
// above), so there is no lazy chunk left to import for it.
const PreOpInboxPage = lazy(
  () => import("./components/admin/preop_lanes/InboxPage"),
);
// PreOp Checklist v2 (2026-09-20) — SalesViewV2 rendered in its
// PREOP_CHECKLIST surface mode, NOT a second grid (v1's dedicated read model
// and its `preop_checklist/` folder were torn down; see
// docs/features/preop/checklist-v2/). The page wrapper mounts
// DashboardContainer fullBleedContent, which is load-bearing: RealtimeProvider
// lives there and is what makes the grid live at all.
const PreopChecklistPage = lazy(() => import("./pages/preop_checklist"));
// PreOp Appointments (2026-09-24) — the SAME component on a third surface,
// whose `layout: "cards"` swaps SalesGrid for SalesCards. Sibling of the
// checklist, not a fork; see docs/features/preop/preop-appointments-cards.md.
const PreopAppointmentsPage = lazy(() => import("./pages/preop_appointments"));
// OR Board (2026-10-07) — the checklist's list again, on the OR_BOARD surface
// whose default view is the `or_board` preset. See surfaces.ts OR_BOARD_SURFACE.
const OrBoardPage = lazy(() => import("./pages/or_board"));
const MessagingPage = lazy(() => import("./pages/admin/messaging"));
const SalesViewV2Page = lazy(() => import("./pages/sales_view_v2"));
const SaleDetailsContainer = lazy(
  () => import("./pages/sale_details_container"),
);
const ChatbotDashboard = lazy(() => import("./pages/chatbot_dashboard"));
const ChatbotConversationDetailContainer = lazy(
  () => import("./pages/chatbot_conversation_detail"),
);
const SurgeonReviewDashboard = lazy(
  () => import("./pages/surgeon_review_dashboard"),
);
const AnalyticsDashboardV2Page = lazy(
  () => import("./pages/analytics_dashboard_v2"),
);
const ReactivationDashboardPage = lazy(() => import("./pages/reactivation"));
const DayZeroConversionDashboardPage = lazy(
  () => import("./pages/day_zero_conversion"),
);
const PaidLeadSlaRoutePage = lazy(() => import("./pages/paid_lead_sla"));
const EnvelopesListPage = lazy(
  () => import("./pages/admin/contracts_v2/envelopes_list_page"),
);
const EnvelopeDetailPage = lazy(
  () => import("./pages/admin/contracts_v2/envelope_detail_page"),
);
const EnvelopeComposerPage = lazy(
  () => import("./pages/admin/contracts_v2/envelope_composer_page"),
);
const TemplateListPage = lazy(
  () => import("./pages/admin/contracts_v2/template_list_page"),
);
const TemplateEditorPage = lazy(
  () => import("./pages/admin/contracts_v2/template_editor_page"),
);
const EmailObservabilityDashboardPage = lazy(
  () => import("./pages/admin/email_observability_dashboard"),
);
const SmsObservabilityDashboardPage = lazy(
  () => import("./pages/admin/sms_observability_dashboard"),
);
const TrackingHealthDashboardPage = lazy(
  () => import("./pages/admin/tracking_health_dashboard"),
);
// Phase 2 — Contracts v2 public signer surface. Mounted as a
// top-level route (NOT under /patient-portal/*) because the
// PatientAuthQuery layout would require a Google OAuth session;
// the signing-link token IS the credential here.
const SignEnvelopePage = lazy(
  () => import("./pages/patient-portal/sign_envelope"),
);
const CampaignsDashboardPage = lazy(
  () => import("./pages/admin/campaigns_dashboard"),
);
const CampaignDetailPage = lazy(() => import("./pages/admin/campaign_page"));
const CampaignsInboxPage = lazy(() => import("./pages/admin/campaigns_inbox"));
const EngagementContactsPage = lazy(
  () => import("./pages/admin/engagement_contacts"),
);
const PaymentsDashboardPage = lazy(
  () => import("./pages/admin/payments_dashboard"),
);
const CorporateCompliancePage = lazy(
  () => import("./pages/admin/corporate_compliance"),
);
const NotificationsDashboardPage = lazy(
  () => import("./pages/admin/notifications_dashboard"),
);
const AirtableBookingsDashboardPage = lazy(
  () => import("./pages/admin/airtable_bookings_dashboard"),
);
// Sales Monitoring — live coordinator activity board (PR 2). ADMIN-only
// server-side (salesMonitoringResolver.ts); no client-side role check here,
// same convention as the other admin dashboards.
const SalesMonitoringDashboardPage = lazy(
  () => import("./pages/admin/sales_monitoring_dashboard"),
);
const BookingCalendarDashboardPage = lazy(
  () => import("./pages/admin/booking_calendar_dashboard"),
);
// Accounting Dashboard ("Books" view) — the page component itself owns the
// header/query-loader/Suspense (see AccountingDashboardPage.tsx), same as
// AnalyticsDashboardV2Page, so the route below doesn't need its own
// <Suspense> wrapper.
const AccountingDashboardPage = lazy(
  () =>
    import("./components/admin/accounting_dashboard/AccountingDashboardPage"),
);
// Financing Review — Cherry-notification quarantine queue. The page component
// itself owns DashboardContainer + AdminMantineProvider (see
// FinancingReviewPage.tsx), same self-contained shape as AccountingDashboardPage.
const FinancingReviewPage = lazy(
  () => import("./components/admin/financing_review/FinancingReviewPage"),
);
// Meta DM monitoring — read-only Messenger/Instagram thread list + transcript
// (Task 9). The page component itself owns DashboardContainer +
// AdminMantineProvider, same self-contained shape as FinancingReviewPage above.
const MetaDmApp = lazy(() => import("./components/admin/meta_dm/MetaDmApp"));
// Meta Knowledge Base — staff-editable clinic facts appended to Ashley's
// Meta DM system prompt (metaDmKnowledgeService.ts). The page component
// itself owns DashboardContainer + AdminMantineProvider, same self-contained
// shape as MetaDmApp above.
const MetaKnowledgePage = lazy(
  () => import("./components/admin/meta_knowledge/MetaKnowledgePage"),
);
// Lead Surface Playbooks — staff-editable, surface-keyed guidance for
// Ashley's SMS lane (surfacePlaybookService.ts). The page component itself
// owns DashboardContainer + AdminMantineProvider, same self-contained shape
// as MetaKnowledgePage above.
const SurfacePlaybooksPage = lazy(
  () => import("./components/admin/surface_playbooks/SurfacePlaybooksPage"),
);
// Automations (Core Data, 2026-09-24 design Phase 2) — admin-authored
// trigger → action rules (to-do / SMS from Ashley / email). The page
// component itself owns DashboardContainer + AdminMantineProvider, same
// self-contained shape as SurfacePlaybooksPage above.
const AutomationsPage = lazy(
  () => import("./components/admin/automations/AutomationsPage"),
);
// Coordinator Guide — static onboarding reference (guideContent.tsx). The
// page component itself owns DashboardContainer + AdminMantineProvider, same
// self-contained shape as FinancingReviewPage above.
const CoordinatorGuidePage = lazy(
  () => import("./components/admin/coordinator_guide/CoordinatorGuidePage"),
);
// Documentation & Guides hub (Task 9) — read-only catalog of internal docs
// (self-contained HTML pages, originally Claude Code / claude.ai artifacts).
// The page component itself owns DashboardContainer + AdminMantineProvider,
// same self-contained shape as CoordinatorGuidePage above.
const AdminDocsPage = lazy(
  () => import("./components/admin/docs_hub/AdminDocsPage"),
);
// Closer Console / AI Audit / AI Closer Trainer pages retired — replaced by the
// Reactivation Dashboard (AI Audit's timeline is absorbed as its ADMIN tab).

// Backwards-compat: the Patients view + standalone detail view were
// renamed (sales_v2 → patients, sale → patient). Coordinators still
// have unread notification-bell links and bookmarked URLs pointing at
// the old paths, so redirect any pre-rename hit to the new equivalent
// while preserving the rest of the path + query string.
const LegacyAdminRedirect = ({ from, to }: { from: string; to: string }) => {
  const { pathname, search } = useLocation();
  const next = pathname.startsWith(from)
    ? to + pathname.slice(from.length) + search
    : to + search;
  return <Navigate to={next} replace />;
};

const router = createBrowserRouter(
  [
    // Public tokenized patient-task pages — declared first so they win
    // against the /* catch-all even if route-specificity ranking misbehaves.
    ...todoRoutes,
    {
      // Public tokenized estimate-acceptance page. Token in the path
      // segment is the credential; no auth/session needed. Server route:
      // GET/POST /api/estimate-accept(/info). See server/routes/estimate-accept.ts.
      // Under /action/ so the pre-React curtain treats it as a non-funnel
      // page (PATIENT_FACING_EMAIL_LINKS.md). Pre-/action/ links still
      // resolve via PublicCatchAllDispatcher's optional (?:action/)? match.
      path: "/action/accept-estimate/:token",
      element: (
        <Suspense fallback={<AcceptEstimateLoadingShell />}>
          <AcceptEstimatePage />
        </Suspense>
      ),
    },
    {
      // Public tokenized booking-fee payment page. Token in the path
      // segment is the credential; no auth/session needed. Server route:
      // GET/POST /api/booking-link(/info|/start-payment). See
      // server/routes/booking-link.ts and
      // docs/payments/14_BOOKING_LINK_FLOW.md.
      // Under /action/ so the pre-React curtain treats it as a non-funnel
      // page (PATIENT_FACING_EMAIL_LINKS.md). Pre-/action/ links still
      // resolve via PublicCatchAllDispatcher's optional (?:action/)? match.
      path: "/action/book/:token",
      element: (
        <Suspense fallback={<BookingLoadingShell />}>
          <BookingPage />
        </Suspense>
      ),
    },
    {
      // Campaign one-click "Book directly" deposit page. Token carries the
      // recipient's contact; the Patient+Quote are materialized at the "Pay"
      // click. Server route: POST /api/book-direct(/info|/prepare-payment),
      // server/routes/book-direct.ts. Under /action/ so the pre-React curtain
      // treats it as a non-funnel page (PATIENT_FACING_EMAIL_LINKS.md).
      path: "/action/book-direct/:token",
      element: (
        <Suspense fallback={<BookDirectLoadingShell />}>
          <BookDirectPage />
        </Suspense>
      ),
    },
    {
      // Public tokenized balance-invoice payment page — partial payments,
      // no login. Token in the path is the credential. Server route:
      // GET/POST /api/invoice-payment-link(/info|/start-payment). See
      // server/routes/invoice-payment-link.ts and
      // docs/payments/15_INVOICE_PAYMENT_LINK.md. Under /action/ so the
      // pre-React curtain treats it as a non-funnel page.
      path: "/action/pay-invoice/:token",
      element: (
        <Suspense fallback={<PayInvoiceLinkLoadingShell />}>
          <PayInvoiceLinkPage />
        </Suspense>
      ),
    },
    {
      // Campaign action link — one-tap photo upload. Token carries the
      // campaign recipient; the Patient+Quote resolve/materialize on first
      // photo send. Server: GET /api/campaign-action/info, POST
      // /api/campaign-action/photos (server/routes/campaign-action-routes.ts).
      // CampaignPhotoPage wraps its lazy content in its own Suspense with
      // CampaignPhotoLoadingShell — don't add another Suspense here (mirrors
      // SignEnvelopePage). Under /action/ so the pre-React curtain treats it
      // as a non-funnel page (PATIENT_FACING_EMAIL_LINKS.md).
      path: "/action/upload-photos/:token",
      element: <CampaignPhotoPage />,
    },
    {
      // Campaign action link — financing trust warm-up → Cherry apply.
      // Token carries the campaign recipient; the apply-click is stamped
      // server-side (resolve-or-create quote). Server: GET
      // /api/campaign-action/info, POST /api/campaign-action/financing-click
      // (server/routes/campaign-action-routes.ts). CampaignFinancingPage
      // wraps its lazy content in its own Suspense — don't add another
      // Suspense here. Under /action/ so the pre-React curtain treats it as
      // a non-funnel page (PATIENT_FACING_EMAIL_LINKS.md).
      path: "/action/apply-financing/:token",
      element: <CampaignFinancingPage />,
    },
    {
      // Password-reset page (P0-2). Token in the path is the single-use
      // 30-min credential emailed to the patient. No login required.
      // Under /action/ so the pre-React curtain is dismissed immediately
      // (PATIENT_FACING_EMAIL_LINKS.md — /action/ rule). Declared above
      // /* so React Router picks it; PublicCatchAllDispatcher has a
      // matching defensive intercept as belt-and-suspenders.
      path: "/action/reset-password/:token",
      element: (
        <Suspense fallback={<ResetPasswordLoadingShell />}>
          <ResetPasswordPage />
        </Suspense>
      ),
    },
    {
      // Expired quote resume link → "Request a new link". Token in the path is
      // only used to recover the quoteId server-side; the re-issue sends a fresh
      // 30-day link to the contact on file. Declared above /* (React Router
      // picks it); PublicCatchAllDispatcher has a matching defensive intercept.
      // Under /action/ so the pre-React curtain treats it as a non-funnel page.
      path: "/action/quote-expired/:token",
      element: (
        <Suspense fallback={<QuoteExpiredLoadingShell />}>
          <QuoteExpiredPage />
        </Suspense>
      ),
    },
    {
      // WellnessCami intake form — patient-facing, token-gated (P5).
      // Declared above /*; PublicCatchAllDispatcher has a matching intercept;
      // under /action/ so the pre-React curtain treats it as a non-funnel page.
      path: "/action/wellness/intake/:token",
      element: (
        <Suspense fallback={<WellnessIntakeLoadingShell />}>
          <WellnessIntakePage />
        </Suspense>
      ),
    },
    {
      // Patient-facing Booking Calendar (Tier Pricing) — public, no auth.
      // Mock scaffold for the Tier Pricing feature; see
      // docs/features/tier-pricing-booking/06_FRONTEND_SCAFFOLD.md and
      // client/src/components/booking-calendar/. Declared above /* so
      // React Router picks the explicit route; PublicCatchAllDispatcher
      // has a matching defensive intercept as a belt-and-suspenders.
      // PATIENT_FACING_EMAIL_LINKS.md compliance: explicit route + catch-
      // all intercept + labelled loading shell + index.html curtain
      // exclusion (added below in isQuotePage).
      // The splat carries the flow step (/booking-calendar/procedures,
      // /medical, /contact, /confirm) — parsed inside BookingCalendarApp.
      path: "/booking-calendar/*",
      element: (
        <Suspense fallback={<BookingCalendarLoadingShell />}>
          <BookingCalendarPage />
        </Suspense>
      ),
    },
    {
      // Former Booking Calendar URL — renamed to /booking-calendar (the
      // bare /book base couldn't carry step path segments: legacy
      // /book/:token booking-fee links own every /book/<segment> URL).
      // Kept as a query-preserving redirect for anything still pointing
      // at /book.
      path: "/book",
      element: <BookRedirect />,
    },
    {
      // OBS Browser Source overlay, declared above /live (next entry) to read in
      // the same order as PublicCatchAllDispatcher's branches; React Router 6
      // ranks the static path above /live regardless. The Suspense fallback AND
      // the errorElement are the transparent OverlayLoadingShell: this route is
      // composited live over a camera with no viewer to read an error.
      path: "/live/overlay",
      element: (
        <OverlaySilentBoundary>
          <Suspense fallback={<OverlayLoadingShell />}>
            <LiveOverlayPage />
          </Suspense>
        </OverlaySilentBoundary>
      ),
      errorElement: <OverlayLoadingShell />,
    },
    {
      // Public livestream viewer — patient-facing marketing URL, NOT under
      // /action/ (same precedent as /booking-calendar above: a marketing
      // entry point, not a token-gated email link). topaesthetics.com/live
      // redirects into the SPA here (sites/topaesthetics.com/.htaccess).
      // Declared above /* so React Router picks the explicit route;
      // PublicCatchAllDispatcher has a matching defensive intercept as
      // belt-and-suspenders. PATIENT_FACING_EMAIL_LINKS.md compliance:
      // explicit route + catch-all intercept + labelled loading shell +
      // index.html curtain exclusion (added below in isQuotePage).
      // See docs/livestreaming/plan.md § "2026-07-21 — Web-first V1 pivot".
      path: "/live",
      element: (
        // The boundary is INSIDE the element, not just the errorElement beside
        // it: React only exposes the component stack to componentDidCatch, and
        // that stack is the whole payload of a /live crash report (a production
        // build's message is "Minified React error #185" and nothing else).
        // See components/livestream/LiveRouteError.tsx.
        <LiveRouteErrorBoundary>
          <Suspense fallback={<LiveStreamLoadingShell />}>
            <LiveStreamPage />
          </Suspense>
        </LiveRouteErrorBoundary>
      ),
      errorElement: <LiveRouteError />,
    },
    {
      // Public livestream REPLAY viewer. The /live route above is EXACT, so
      // without this entry the texted link falls through to /*, which is why
      // PublicCatchAllDispatcher carries the matching intercept as well.
      // PATIENT_FACING_EMAIL_LINKS.md compliance: explicit route + catch-all
      // intercept + labelled loading shell + index.html curtain exclusion (the
      // isQuotePage /live rule already covers every /live/… path).
      path: "/live/replay/:eventId",
      element: (
        // Boundary INSIDE the element for the reason the /live route above
        // documents: componentDidCatch is the only place React hands over a
        // component stack, and that stack is the crash report.
        <LiveRouteErrorBoundary>
          <Suspense fallback={<LiveReplayLoadingShell />}>
            <LiveReplayPage />
          </Suspense>
        </LiveRouteErrorBoundary>
      ),
      errorElement: <LiveRouteError />,
    },
    {
      path: "/admin/",
      element: (
        <div>
          {/* Accountant-only sessions never mount the Sales View (its
              page-level queries are admin-gated) — they land on Accounting.
              OR-only sessions land on the surgery calendar.
              See AdminHomeRedirect. */}
          <AdminHomeRedirect>
            <Suspense fallback={<SalesViewLoadingShell />}>
              <SalesViewV2Page />
            </Suspense>
          </AdminHomeRedirect>
        </div>
      ),
    },

    {
      path: "/admin/users",
      element: (
        <div>
          <UserDashboard />
        </div>
      ),
    },

    {
      path: "/admin/procedure_types_dashboard",
      element: (
        <div>
          <ProcedureTypesDashboard />
        </div>
      ),
    },
    {
      path: "admin/surgeon_dashboard",
      element: (
        <div>
          <SurgeonDashboard />
        </div>
      ),
    },
    {
      path: "admin/procedures_dashboard",
      element: (
        <div>
          <ProceduresDashboard />
        </div>
      ),
    },
    // Legacy routes — keep so existing bookmarks don't 404. Both render the
    // identical dashboard as the plain route above (no route-specific
    // filtering ever existed) — pass the pre-2026-08-16 "all" default
    // explicitly so their unchanged behavior survives the plain route's
    // cold-default flip to Public (ProceduresList's own default).
    {
      path: "admin/procedures_dashboard_internal",
      element: (
        <div>
          <ProceduresDashboard defaultInternalOnlyFilter="all" />
        </div>
      ),
    },
    {
      path: "admin/procedures_dashboard_external",
      element: (
        <div>
          <ProceduresDashboard defaultInternalOnlyFilter="all" />
        </div>
      ),
    },
    {
      path: "admin/business_locations_dashboard",
      element: (
        <div>
          <BusinessLocationsDashboard />
        </div>
      ),
    },
    {
      path: "admin/patients",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <SalesViewV2Page />
          </Suspense>
        </div>
      ),
    },
    {
      // WellnessCami workspace home (Zen-style workspace switcher). Placeholder
      // content reuses the patients spreadsheet for now — see wellness_home.tsx.
      path: "admin/wellness",
      element: (
        <div>
          <WellnessHomePage />
        </div>
      ),
    },
    {
      // WellnessCami customization tab — live sidebar theming + brand text.
      path: "admin/wellness/customization",
      element: (
        <div>
          <WellnessCustomizationPage />
        </div>
      ),
    },
    // --- WellnessCami portal sections (frontend scaffold) -----------------
    {
      path: "admin/wellness/overview",
      element: (
        <div>
          <WellnessOverviewPage />
        </div>
      ),
    },
    {
      path: "admin/wellness/recipes",
      element: (
        <div>
          <WellnessRecipesPage />
        </div>
      ),
    },
    {
      path: "admin/wellness/clients",
      element: (
        <div>
          <WellnessClientsPage />
        </div>
      ),
    },
    {
      path: "admin/wellness/patient-calendar",
      element: (
        <div>
          <WellnessPatientCalendarPage />
        </div>
      ),
    },
    {
      path: "admin/wellness/intake",
      element: (
        <div>
          <WellnessIntakeAdminPage />
        </div>
      ),
    },
    {
      path: "admin/wellness/messages",
      element: (
        <div>
          <WellnessMessagingPage />
        </div>
      ),
    },
    {
      // WellnessCami routed patient popup. Renders the same wellness home grid
      // underneath; SalesViewV2 reads useParams().patient_id and — because
      // this path is under /admin/wellness (workspaceIdForPath → "wellness")
      // — mounts the wellness profile modal instead of the clinic one. The
      // optional :quote_id/:tab segments mirror the clinic route so the
      // embedded clinic record's tab/quote navigation keeps the modal open.
      path: "admin/wellness/patients/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <WellnessHomePage />
        </div>
      ),
    },
    {
      // Routed modal — rendering the same page component so the grid
      // stays mounted underneath. The page reads `useParams().patient_id`
      // and renders the detail modal as an overlay when present. The
      // optional :quote_id/:tab segments mirror v1's URL shape so
      // SaleDetails' internal tab and quote-switch navigation stays
      // inside the modal context.
      path: "admin/patients/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <SalesViewV2Page />
          </Suspense>
        </div>
      ),
    },
    // Pre-rename redirects (see LegacyAdminRedirect note above).
    {
      path: "admin/sales_v2",
      element: <Navigate to="/admin/patients" replace />,
    },
    {
      path: "admin/sales_v2/*",
      element: (
        <LegacyAdminRedirect from="/admin/sales_v2" to="/admin/patients" />
      ),
    },
    {
      path: "admin/sale/*",
      element: <LegacyAdminRedirect from="/admin/sale" to="/admin/patient" />,
    },
    {
      path: "admin/surgeon_review",
      element: (
        <div>
          <SurgeonReviewDashboard />
        </div>
      ),
    },
    {
      path: "admin/calendar_dashboard",
      element: (
        <div>
          <CalendarDashboard />
        </div>
      ),
    },
    {
      path: "admin/analytics_dashboard_v2",
      element: (
        <div>
          <AnalyticsDashboardV2Page />
        </div>
      ),
    },
    {
      path: "admin/accounting",
      element: (
        <div>
          <AccountingDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/financing-review",
      element: (
        <div>
          <FinancingReviewPage />
        </div>
      ),
    },
    {
      path: "admin/meta-dm",
      element: (
        <div>
          <MetaDmApp />
        </div>
      ),
    },
    {
      path: "admin/engagement/meta-knowledge",
      element: (
        <div>
          <MetaKnowledgePage />
        </div>
      ),
    },
    {
      path: "admin/engagement/surface-playbooks",
      element: (
        <div>
          <SurfacePlaybooksPage />
        </div>
      ),
    },
    {
      path: "admin/automations",
      element: (
        <div>
          <AutomationsPage />
        </div>
      ),
    },
    {
      // Replaces Closer Console / AI Audit / AI Closer Trainer. Page supplies
      // its own <Suspense> (see pages/reactivation.tsx).
      path: "admin/reactivation",
      element: (
        <div>
          <ReactivationDashboardPage />
        </div>
      ),
    },
    {
      // Page supplies its own <Suspense> (see pages/day_zero_conversion.tsx).
      path: "admin/day_zero_conversion",
      element: (
        <div>
          <DayZeroConversionDashboardPage />
        </div>
      ),
    },
    {
      // Paid Google leads speed-to-human + per-campaign $/booked scoreboard
      // (server/schema/_paidLeadSla.graphql). Page supplies its own <Suspense>
      // (see pages/paid_lead_sla.tsx).
      path: "admin/paid_leads_sla",
      element: (
        <div>
          <PaidLeadSlaRoutePage />
        </div>
      ),
    },
    {
      path: "admin/email-observability",
      element: (
        <div>
          <EmailObservabilityDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/sms-observability",
      element: (
        <div>
          <SmsObservabilityDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/tracking-health",
      element: (
        <div>
          <TrackingHealthDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/campaigns",
      element: (
        <div>
          <CampaignsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/campaigns-inbox",
      element: (
        <div>
          <CampaignsInboxPage />
        </div>
      ),
    },
    {
      // Static before dynamic — /new must not match the :id route.
      path: "admin/campaigns/new",
      element: (
        <div>
          <CampaignDetailPage />
        </div>
      ),
    },
    {
      path: "admin/campaigns/:id",
      element: (
        <div>
          <CampaignDetailPage />
        </div>
      ),
    },
    {
      path: "admin/engagement/contacts",
      element: (
        <div>
          <EngagementContactsPage />
        </div>
      ),
    },
    {
      path: "admin/patients/payments",
      element: (
        <div>
          <PaymentsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/management/corporate_compliance",
      element: (
        <div>
          <CorporateCompliancePage />
        </div>
      ),
    },
    {
      path: "admin/notifications",
      element: (
        <div>
          <NotificationsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/patients/airtable-bookings",
      element: (
        <div>
          <AirtableBookingsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/sales_monitoring",
      element: (
        <div>
          <SalesMonitoringDashboardPage />
        </div>
      ),
    },
    {
      path: "/admin/booking-calendar",
      element: (
        <div>
          <BookingCalendarDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/patient/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <SaleDetailsContainer />
        </div>
      ),
    },
    {
      path: "admin/chatbot",
      element: (
        <div>
          <ChatbotDashboard />
        </div>
      ),
    },
    {
      path: "admin/chatbot/:id",
      element: (
        <div>
          <ChatbotConversationDetailContainer />
        </div>
      ),
    },
    {
      path: "admin/patients_activity",
      element: (
        <div>
          <PatientsActivityPage />
        </div>
      ),
    },
    {
      path: "admin/ab_test_results",
      element: (
        <div>
          <ABTestResultsDashboard />
        </div>
      ),
    },
    {
      path: "admin/promo_codes_dashboard",
      element: (
        <div>
          <PromoCodesDashboard />
        </div>
      ),
    },
    {
      path: "admin/roadmap_dashboard",
      element: (
        <div>
          <RoadmapDashboard />
        </div>
      ),
    },
    {
      path: "admin/labs_dashboard",
      element: (
        <div>
          <LabsDashboard />
        </div>
      ),
    },
    {
      path: "admin/postop-locations",
      element: (
        <div>
          <PostOpLocationsDashboard />
        </div>
      ),
    },
    {
      path: "admin/coordinator_view_dashboard",
      element: (
        <div>
          <CoordinatorViewDashboard />
        </div>
      ),
    },
    {
      path: "admin/guide",
      element: (
        <div>
          <CoordinatorGuidePage />
        </div>
      ),
    },
    {
      path: "admin/docs",
      element: (
        <div>
          <AdminDocsPage />
        </div>
      ),
    },
    {
      path: "/*",
      element: (
        <div>
          <PublicCatchAllDispatcher />
        </div>
      ),
      children: [
        { path: "booking-success", element: <BookingSuccessPage /> },
        { path: "booking-cancelled", element: <BookingCancelledPage /> },
      ],
    },
    {
      path: "/id/:quote_id/*",
      element: (
        <div>
          <Quote />
        </div>
      ),
    },
    {
      path: "/admin/contract-editor/:contract_template_id?",
      element: (
        <div>
          <Suspense>
            <ContractTemplateEditorDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/statuses-dashboard/:tab?",
      element: (
        <div>
          <Suspense>
            <StatusesDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/ringcentral_auth",
      element: (
        <div>
          <Suspense>
            <RingCentralAuthPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/settings/quickbooks",
      element: (
        <div>
          <Suspense>
            <QuickBooksSettingsPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/settings/quickbooks/sync-status",
      element: (
        <div>
          <Suspense>
            <QBOSyncStatusPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/settings/feature-flags",
      element: (
        <div>
          <Suspense>
            <FeatureFlagsAdminPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/medical-staff",
      element: (
        <div>
          <Suspense>
            <MedicalStaffAdminPage />
          </Suspense>
        </div>
      ),
    },
    // Bookmarks from the retired 2026-10-07 Surgical Team page.
    {
      path: "/admin/settings/surgical-team",
      element: <Navigate to="/admin/medical-staff" replace />,
    },
    {
      path: "/admin/settings/campaign-manager",
      element: (
        <div>
          <Suspense>
            <CampaignManagerSettingsAdminPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/voice-agent",
      element: (
        <div>
          <Suspense>
            <VoiceAgentPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/payments_dashboard/reconciliation",
      element: (
        <div>
          <Suspense>
            <ReconciliationDashboardPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/contract-templates-dashboard",
      element: (
        <div>
          <Suspense>
            <ContractTemplatesDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/sms-templates",
      element: (
        <div>
          <Suspense>
            <SmsTemplatesDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/labs-request-text-templates",
      element: (
        <div>
          <Suspense>
            <LabsRequestTextTemplatesDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/addons-dashboard",
      element: (
        <div>
          <Suspense>
            <AddonsDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/products-dashboard",
      element: (
        <div>
          <Suspense>
            <ProductsDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/photo-library",
      element: (
        <div>
          <Suspense>
            <PhotoLibraryPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "admin/marketing_dashboard_v2",
      element: (
        <div>
          <MarketingDashboardV2Page />
        </div>
      ),
    },
    {
      path: "admin/active_campaigns",
      element: (
        <div>
          <ActiveCampaignsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/content-engagement",
      element: (
        <div>
          <ContentEngagementDashboardPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      path: "admin/employee-engagement",
      element: (
        <div>
          <EmployeeEngagementDashboardPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      path: "admin/campaign-manager",
      element: (
        <div>
          <CampaignManagerDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/third_party_usage",
      element: (
        <div>
          <ThirdPartyUsageDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/gallery_photos_dashboard",
      element: (
        <div>
          <GalleryPhotosDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/acknowledgements_dashboard",
      element: (
        <div>
          <AcknowledgementsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/guides",
      element: (
        <div>
          <GuidesPage />
        </div>
      ),
    },
    {
      // ":id" is "new" when creating — see GuideEditorPage.
      path: "admin/guides/:id",
      element: (
        <div>
          <GuideEditorPage />
        </div>
      ),
    },
    {
      path: "admin/testimonials_dashboard",
      element: (
        <div>
          <TestimonialsDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/resources_dashboard",
      element: (
        <div>
          <ResourcesDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/usability_dashboard",
      element: (
        <div>
          <UsabilityDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/ios_analytics",
      element: (
        <div>
          <IosAnalyticsPage />
        </div>
      ),
    },
    {
      path: "admin/traffic_dashboard",
      element: (
        <div>
          <TrafficDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/funnel_flow",
      element: (
        <div>
          <FunnelFlowDashboardPage />
        </div>
      ),
    },
    {
      path: "admin/live",
      element: (
        <div>
          <LiveDashboardPage />
        </div>
      ),
    },
    {
      // Value-based sibling of admin/live directly above — same wallboard,
      // booked dollar value as the headline numbers (mode="value").
      path: "admin/live-value-based",
      element: (
        <div>
          <LiveValueDashboardPage />
        </div>
      ),
    },
    {
      // Retired single-console route. Lands on the Stream dashboard (Oct 17
      // 2026, R2). `replace` keeps it out of history so Back doesn't bounce
      // off the redirect.
      path: "admin/livestream",
      element: <Navigate to="/admin/livestream/dashboard" replace />,
    },
    {
      // Stream dashboard: the all-in-one show page (Oct 17 2026). Page
      // supplies its own <Suspense>, like the routes below.
      path: "admin/livestream/dashboard",
      element: (
        <div>
          <LivestreamStreamDashboardPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      // Stream overview: the broadcasting half (LivestreamDashboardPage, at
      // /dashboard until Oct 17 2026).
      path: "admin/livestream/overview",
      element: (
        <div>
          <LivestreamDashboardPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      // Stream planning: the planning half (LivestreamOverviewPage, at
      // /overview until Oct 17 2026). Page supplies its own <Suspense> (see
      // pages/admin/LivestreamOverviewPage.tsx), same as admin/reactivation /
      // admin/day_zero_conversion above — no extra wrapper needed here.
      path: "admin/livestream/planning",
      element: (
        <div>
          <LivestreamOverviewPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      // Livestream admin — post-hoc analytics (PR 5). Absorbs the read-only
      // "Reviewing" Q&A view that used to render inline on Stream planning.
      // Event selection rides `?event=<id>` so the page is deep-linkable from
      // the Stream planning events table.
      path: "admin/livestream/analytics",
      element: (
        <div>
          <LivestreamAnalyticsPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      // Subscribers view (PR-B) — one row per patient across all three
      // livestream signup lanes, replacing the Signups section that used to
      // live on Stream planning. Page supplies its own <Suspense> (see
      // pages/admin/LivestreamSubscribersPage.tsx), same as the routes
      // above.
      path: "admin/livestream/subscribers",
      element: (
        <div>
          <LivestreamSubscribersPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      // Promotion: every livestream tracking link with what it brought in
      // (PR B) and the message wording (PR C). The page wraps each tab in
      // its own <Suspense>, same as the routes above.
      path: "admin/livestream/promotion",
      element: (
        <div>
          <LivestreamPromotionPage />
        </div>
      ),
      errorElement: <AdminRouteError />,
    },
    {
      // Chat pop-out window. NO wrapping <div> and no DashboardContainer: it
      // owns its whole (small) window.
      path: "admin/livestream/chat-popout",
      element: <LivestreamChatPopoutPage />,
      errorElement: <AdminRouteError />,
    },
    {
      path: "admin/action_queues",
      element: (
        <div>
          <ActionQueuesPage />
        </div>
      ),
    },
    {
      path: "admin/preop",
      element: (
        <div>
          <PreOpDashboardPage />
        </div>
      ),
    },
    {
      // Mantine case workspace — mounts its own <Suspense>/AdminMantineProvider
      // (see PreOpCaseWorkspacePage.tsx), same as admin/reactivation /
      // admin/day_zero_conversion above. `?tab=` selects a tab (retired names
      // still resolve) and `?doc=` opens the Labs Intake tab on that document —
      // both read by the page itself, so no routing wrapper stands in front of
      // it.
      path: "admin/preop/case/:caseId",
      element: (
        <div>
          <PreOpCaseWorkspacePage />
        </div>
      ),
    },
    {
      // The INTAKE QUEUE — documents that arrived on a channel and are not on
      // anybody's chart yet. Mounts its own <Suspense>/AdminMantineProvider,
      // same self-contained shape as the case workspace above.
      //
      // This was "Lane A — filing" (a patient rail + viewer, scoped by `?case=`
      // and `?doc=`) until 2026-08-28, when the owner retired the filing lane
      // here for the same reason the Clear Labs lane went a day earlier: work
      // across patients, away from the patient, is confusing and error-prone.
      // Filing is per-case now — the case workspace's Labs Intake tab. The page
      // ignores `?case=`/`?doc=`; every caller that still builds such a URL
      // (laneRoutes' inboxUrl) was already unreachable before the retirement —
      // three as fallbacks behind an `onGoToDocuments` handler every live mount
      // supplies, one inside ClearancesContent's never-rendered `!embedded`
      // branch.
      path: "admin/preop/inbox",
      element: (
        <div>
          <PreOpInboxPage />
        </div>
      ),
    },
    {
      // PreOp Checklist — SalesViewV2 on the PREOP_CHECKLIST surface (v2).
      // Route-level <Suspense> with SalesViewLoadingShell, matching
      // admin/patients: the page wrapper has its own Suspense, but a
      // CHUNK-load suspend happens before that element ever mounts and would
      // otherwise fall through to the app-root <Suspense fallback={null}> and
      // blank the whole page.
      path: "admin/preop/checklist",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <PreopChecklistPage />
          </Suspense>
        </div>
      ),
    },
    {
      // Routed modal over the checklist — the same pair admin/patients
      // carries. Rendering the SAME page component keeps the grid mounted
      // underneath; SalesViewV2 reads useParams().patient_id and mounts
      // SaleDetailModal as an overlay, with basePath/closeTo taken off the
      // surface so closing returns to /admin/preop/checklist, never
      // /admin/patients. The optional :quote_id/:tab segments let
      // SaleDetails' internal tab + quote-switch navigation stay inside the
      // modal context.
      path: "admin/preop/checklist/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <PreopChecklistPage />
          </Suspense>
        </div>
      ),
    },
    {
      // PreOp Appointments — SalesViewV2 on the PREOP_APPOINTMENTS surface,
      // rendered as cards. Same route-level <Suspense> reasoning as the
      // checklist pair above: a CHUNK-load suspend happens before the page's
      // own Suspense ever mounts.
      path: "admin/preop/appointments",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <PreopAppointmentsPage />
          </Suspense>
        </div>
      ),
    },
    {
      // Routed modal over the cards — the same pair the checklist carries,
      // for the same reason: rendering the SAME page component keeps the card
      // list mounted underneath while SaleDetailModal opens over it, and
      // `detailRouteBase` on the surface is what sends it here rather than to
      // /admin/preop/checklist.
      path: "admin/preop/appointments/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <PreopAppointmentsPage />
          </Suspense>
        </div>
      ),
    },
    {
      // OR Board — SalesViewV2 on the OR_BOARD surface (the checklist's list,
      // opened on the `or_board` preset). Same route-level <Suspense> reasoning
      // as the pairs above.
      path: "admin/preop/or-board",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <OrBoardPage />
          </Suspense>
        </div>
      ),
    },
    {
      // Routed modal over the board — `detailRouteBase` on the surface sends
      // closing back here rather than to /admin/preop/checklist.
      path: "admin/preop/or-board/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <OrBoardPage />
          </Suspense>
        </div>
      ),
    },
    {
      // Post Op Visits - the Glide grid of post-op visits (the visit drawer
      // rides on ?visit=<id>). Same route-level <Suspense> reasoning as the
      // pairs above.
      path: "admin/patients/postop-visits",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <PostOpVisitsDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      // Routed patient-file modal over the grid (SaleDetailModal) - the same
      // pair the checklist / OR Board carry. Static "postop-visits" outranks
      // admin/patients/:patient_id, so this never reads as a patient id.
      path: "admin/patients/postop-visits/:patient_id/:quote_id?/:tab?",
      element: (
        <div>
          <Suspense fallback={<SalesViewLoadingShell />}>
            <PostOpVisitsDashboard />
          </Suspense>
        </div>
      ),
    },
    {
      // The retired Clear Labs lane (owner decision 2026-08-28). Kept as a
      // redirect for the same reason /admin/preop/documents is — bookmarks and
      // older links point here — but clearing now happens only inside a case,
      // on the workspace's Labs Review tab, so this lands on the queue.
      path: "admin/preop/clearances",
      element: <PreOpClearancesRedirect />,
    },
    {
      // The retired global documents queue. Kept as a redirect (never removed
      // outright) — the dashboard banner, bookmarks and older links all point
      // here, and the filing lane is where that work happens now.
      path: "admin/preop/documents",
      element: <PreOpDocumentsRedirect />,
    },
    {
      // Trailing patient_id/quote_id/tab are optional so Messaging stays
      // mounted (deep-linkable / refresh-safe) when a conversation's linked
      // patient-file modal is open — mirrors admin/patients/:patient_id/:quote_id?/:tab?
      // above. Route-driven modal: MessagingApp.tsx reads patient_id via
      // useParams and mounts SaleDetailModal directly off that param.
      path: "admin/messaging/:patient_id?/:quote_id?/:tab?",
      element: (
        <div>
          <MessagingPage />
        </div>
      ),
    },
    {
      path: "admin/usability/quote/:quote_id",
      element: (
        <div>
          <UsabilityQuoteDrilldownPage />
        </div>
      ),
    },
    {
      path: "/admin/configs_dashboard",
      element: (
        <div>
          <Suspense>
            <ConfigsDashboard />
          </Suspense>
        </div>
      ),
    },

    {
      path: "/admin/contracts/envelopes",
      element: (
        <div>
          <Suspense>
            <EnvelopesListPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/contracts/envelopes/new",
      element: (
        <div>
          <Suspense>
            <EnvelopeComposerPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/contracts/envelopes/:id",
      element: (
        <div>
          <Suspense>
            <EnvelopeDetailPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/contracts/templates",
      element: (
        <div>
          <Suspense>
            <TemplateListPage />
          </Suspense>
        </div>
      ),
    },
    {
      path: "/admin/contracts/templates/:id",
      element: (
        <div>
          <Suspense>
            <TemplateEditorPage />
          </Suspense>
        </div>
      ),
    },

    {
      // Phase 2 — Contracts v2 public signer page. Token in `?t=`.
      // SignEnvelopePage wraps SignerApp in its own Suspense with the
      // SignerLoadingShell fallback — don't add another Suspense here.
      path: "/portal/sign/:envelopeId",
      element: (
        <div>
          <SignEnvelopePage />
        </div>
      ),
    },

    ...patientPortalRoutes,
    ...dynamicFormRoutes,
  ],
  {
    basename: process.env.REACT_APP_BASENAME,
  },
);

// Install global error handlers + page-unload flush before anything else
// so we capture errors thrown during early bootstrapping too.
installGlobalErrorHandlers();

// Fire a one-shot ping confirming the JS bundle started executing on this
// device. Defends against survivorship bias when render-time bugs hide a
// device class (e.g. Samsung Internet) from our in-app tracking.
emitBootstrapPing();

// Persist tracking identifiers and lead origin from the landing URL.
(function persistTracking() {
  try {
    persistTrackingFromUrl(
      window.location.search,
      document.referrer,
      window.location.hostname,
    );
  } catch {
    // ignore
  }
})();

// Pairs with client_bootstrap. Bootstrap fires before the JS bundle finishes
// downloading; this event fires the moment the bundle has finished parsing
// and we are about to call createRoot. The gap between the two times the
// bundle's download+parse cost; the gap from here to quote_step_view times
// React mount + Relay init. Lets us split "bounced during bundle load" from
// "bounced during hydration" — the two failure modes look identical on a
// single bootstrap-only ghost session.
trackEvent("client_bundle_ready", {
  documentReadyState: document.readyState,
  timeSincePageLoadMs:
    typeof performance !== "undefined" ? Math.round(performance.now()) : null,
});

// HTML curtain (#initial-loading) stays visible until QuoteApp is actually
// ready and calls window.hideInitialLoading(). React boots invisibly behind
// it so transient Suspense fallbacks don't flicker through.
createRoot(document.getElementById("root") as HTMLElement).render(
  <Suspense fallback={null}>
    <ErrorBoundary>
      <AuthenticationProvider>
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <BusinessLocationProvider>
            <AppConfigProvider>
              <GAProvider measurementId={frontEnv.GA_MEASUREMENT_ID}>
                <FeatureFlagsProvider>
                  <ABTestProvider storageType="cookies">
                    <ABTestResolutionProvider>
                      <WorkspaceProvider>
                        <WellnessCustomizationProvider>
                          <RouterProvider router={router} />
                          {/* The OBS overlay draws nothing but its own card,
                              not even this dev-only debug button. */}
                          {IS_OBS_OVERLAY ? null : <ABTestDebugWindow />}
                        </WellnessCustomizationProvider>
                      </WorkspaceProvider>
                    </ABTestResolutionProvider>
                  </ABTestProvider>
                </FeatureFlagsProvider>
              </GAProvider>
            </AppConfigProvider>
          </BusinessLocationProvider>
        </RelayEnvironmentProvider>
      </AuthenticationProvider>
    </ErrorBoundary>
  </Suspense>,
);
